import type {
    AnyObjectType,
    ObjectTypename,
    ConnectionTypename,
    EdgeTypename,
} from '../derived';

export type ExtractTypename<T, K extends ObjectTypename> = Extract<
    T,
    { __typename?: K }
>;

/**
 * Checks if a GraphQL-object has the provided typename.
 *
 * @param obj
 * @param typename
 */
export function isType<T extends AnyObjectType, K extends ObjectTypename>(
    obj: T | null | undefined,
    typename: K
): obj is ExtractTypename<T, K> {
    if (!obj) return false;
    if ('__typename' in obj == false) return false;
    return obj.__typename === typename;
}

/**
 * Checks if a GraphQL-object could have the provided typename.
 *
 * Can be useful for when the object itself isn't loaded yet.
 *
 * @param value
 * @param typename
 */
export function couldBeType<T extends AnyObjectType, K extends ObjectTypename>(
    value: null | undefined | T,
    typename: K
): value is null | undefined | ExtractTypename<T, K> {
    return (
        !value ||
        value.__typename === undefined ||
        value.__typename === typename
    );
}

/**
 * Returns the value iff the value had the provided typename.
 *
 * @param value
 * @param typename
 */
export function asType<T extends AnyObjectType, K extends ObjectTypename>(
    value: T | null | undefined,
    typename: K
): ExtractTypename<T, K> | undefined {
    if (isType(value, typename)) return value;
    return undefined;
}

/**
 * Checks whether the provided object is a `Connection`-object.
 *
 * @param obj The object to check.
 */
export function isConnection<T extends AnyObjectType>(
    obj: T | null | undefined
): obj is ExtractTypename<T, ConnectionTypename> {
    return (
        !!obj &&
        typeof obj.__typename === 'string' &&
        obj.__typename.endsWith('Connection')
    );
}

/**
 * Checks whether the provided object is a `Edge`-object.
 *
 * @param obj The object to check.
 */
export function isEdge<T extends AnyObjectType>(
    obj: T | null | undefined
): obj is ExtractTypename<T, EdgeTypename> {
    return (
        !!obj &&
        typeof obj.__typename === 'string' &&
        obj.__typename.endsWith('Edge')
    );
}
